import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createColumnHelper } from '@tanstack/react-table';
import BigNumber from 'big.js';
import { StyleSheet } from 'react-native';
import { theme } from '@xbto/design-system';
import { Amount, AmountChangePercent, AmountPercent, AmountPrice, AmountQuantity, CurrencyIdentity, } from '../../organisms';
import { Col } from '../../atoms';
import { NoDataPlaceholder } from '../../molecules';
import { FUND_TABLE_HEADER_CELL_TEMPLATES } from '../../utils/templates';
import { PerformanceSettings } from '../fund-performance-in-select';
function isMuted(asset) {
    var _a;
    return !!((_a = asset.fundDetails) === null || _a === void 0 ? void 0 : _a.isRedeemed);
}
const columnHelper = createColumnHelper();
const flexEndMeta = { justify: 'flex-end', isMuted };
const COLUMN = {
    FUND_NAME: columnHelper.accessor('currency', {
        cell: info => {
            const val = info.getValue();
            if (!(val === null || val === void 0 ? void 0 : val.code)) {
                return;
            }
            return (_jsx(CurrencyIdentity, { apy: BigNumber(val.apy), code: val.code, displayCode: null, name: val.name }));
        },
        size: 232,
        header: 'Fund name',
        meta: {
            justify: 'flex-start',
            autoWidth: true,
            isMuted,
        },
    }),
    TOTAL_INVESTED_IN_ASSET: columnHelper.accessor('fundDetails', {
        id: 'totalInvestedInAsset',
        cell: info => {
            var _a;
            const fundDetails = info.getValue();
            if ((fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.isRedeemed) ||
                !(fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.totalInvested) ||
                !((_a = fundDetails.secondaryAsset) === null || _a === void 0 ? void 0 : _a.displayCode)) {
                return _jsx(NoDataPlaceholder, {});
            }
            return (_jsx(AmountQuantity, { currencyCode: fundDetails.secondaryAsset.displayCode, value: BigNumber(fundDetails.totalInvested), color: "primary" }));
        },
        size: 180,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.TOTAL_NET_INVESTED,
        meta: flexEndMeta,
    }),
    TOTAL_INVESTED_IN_USD: columnHelper.accessor('fundDetails', {
        id: 'totalInvestedInUsd',
        cell: info => {
            const fundDetails = info.getValue();
            if ((fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.isRedeemed) || !(fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.totalInvestedUsd)) {
                return _jsx(NoDataPlaceholder, {});
            }
            return (_jsx(AmountQuantity, { currencyCode: 'USD', value: BigNumber(fundDetails.totalInvestedUsd), color: "primary" }));
        },
        size: 160,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.TOTAL_NET_INVESTED,
        meta: flexEndMeta,
    }),
    NO_OF_SHARES: columnHelper.accessor('fundDetails', {
        id: 'noOfShares',
        cell: info => {
            const fundDetails = info.getValue();
            if (!fundDetails ||
                fundDetails.isRedeemed ||
                fundDetails.shares === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(Amount, { color: "primary", value: BigNumber(fundDetails.shares) });
        },
        size: 128,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.NO_OF_SHARES,
        meta: flexEndMeta,
    }),
    VALUE_IN_ASSET: columnHelper.accessor('fundDetails', {
        id: 'valueInAsset',
        cell: info => {
            var _a;
            const fundDetails = info.getValue();
            const secondaryAssetValue = fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.valueInSecondaryAsset;
            const secondaryAssetCode = (_a = fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.secondaryAsset) === null || _a === void 0 ? void 0 : _a.displayCode;
            if (!fundDetails ||
                fundDetails.isRedeemed ||
                !secondaryAssetValue ||
                !secondaryAssetCode) {
                return _jsx(NoDataPlaceholder, {});
            }
            const asset = info.row.original;
            return (_jsxs(Col.R, { children: [_jsx(AmountQuantity, { value: BigNumber(secondaryAssetValue), currencyCode: secondaryAssetCode, color: "primary" }), asset.quantityIncludingPendingUsd !== null && (_jsx(Col, { style: $.mt, children: _jsx(AmountPrice, { small: true, value: BigNumber(asset.quantityIncludingPendingUsd) }) }))] }));
        },
        size: 160,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.TOTAL_VALUE,
        meta: flexEndMeta,
    }),
    VALUE_IN_USD: columnHelper.accessor('fundDetails', {
        id: 'valueInUsd',
        cell: info => {
            var _a;
            const fundDetails = info.getValue();
            const asset = info.row.original;
            if (!fundDetails ||
                fundDetails.isRedeemed ||
                asset.quantityIncludingPendingUsd === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            const secondaryAssetValue = fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.valueInSecondaryAsset;
            const secondaryAssetCode = (_a = fundDetails === null || fundDetails === void 0 ? void 0 : fundDetails.secondaryAsset) === null || _a === void 0 ? void 0 : _a.displayCode;
            return (_jsxs(Col.R, { children: [_jsx(AmountPrice, { color: "primary", value: BigNumber(asset.quantityIncludingPendingUsd) }), !!secondaryAssetValue && !!secondaryAssetCode && (_jsx(Col, { style: $.mt, children: _jsx(AmountQuantity, { value: BigNumber(secondaryAssetValue), currencyCode: secondaryAssetCode, small: true }) }))] }));
        },
        size: 160,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.TOTAL_VALUE,
        meta: flexEndMeta,
    }),
    ALLOCATION: columnHelper.accessor('allocation', {
        cell: info => {
            const allocation = info.getValue();
            const fundDetails = info.row.original.fundDetails;
            if (!fundDetails || fundDetails.isRedeemed || allocation === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountPercent, { value: BigNumber(allocation), color: "primary" });
        },
        size: 108,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.ALLOCATION,
        meta: flexEndMeta,
    }),
    PTD_PERCENTAGE_IN_ASSET: columnHelper.accessor('ptdPercent', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 160,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.PAST_MONTH_PERF,
        meta: flexEndMeta,
    }),
    PTD_PERCENTAGE_IN_USD: columnHelper.accessor('ptdPercentUsd', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 160,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.PAST_MONTH_PERF,
        meta: flexEndMeta,
    }),
    YTD_PERCENTAGE_IN_ASSET: columnHelper.accessor('ytdPercent', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 108,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.YTD,
        meta: flexEndMeta,
    }),
    YTD_PERCENTAGE_IN_USD: columnHelper.accessor('ytdPercentUsd', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 108,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.YTD,
        meta: flexEndMeta,
    }),
    ITD_PERCENT_IN_ASSET: columnHelper.accessor('allReturnsPercent', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 108,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.ITD,
        meta: flexEndMeta,
    }),
    ITD_PERCENT_IN_USD: columnHelper.accessor('allReturnsPercentUsd', {
        cell: info => {
            const value = info.getValue();
            if (value === null) {
                return _jsx(NoDataPlaceholder, {});
            }
            return _jsx(AmountChangePercent, { value: BigNumber(value) });
        },
        size: 108,
        header: FUND_TABLE_HEADER_CELL_TEMPLATES.ITD,
        meta: flexEndMeta,
    }),
};
export const getColumns = (type) => {
    if (type === PerformanceSettings.ASSET_ACCUMULATION) {
        return [
            COLUMN.FUND_NAME,
            COLUMN.TOTAL_INVESTED_IN_ASSET,
            COLUMN.NO_OF_SHARES,
            COLUMN.VALUE_IN_ASSET,
            COLUMN.ALLOCATION,
            COLUMN.PTD_PERCENTAGE_IN_ASSET,
            COLUMN.YTD_PERCENTAGE_IN_ASSET,
            COLUMN.ITD_PERCENT_IN_ASSET,
        ];
    }
    return [
        COLUMN.FUND_NAME,
        COLUMN.TOTAL_INVESTED_IN_USD,
        COLUMN.NO_OF_SHARES,
        COLUMN.VALUE_IN_USD,
        COLUMN.ALLOCATION,
        COLUMN.PTD_PERCENTAGE_IN_USD,
        COLUMN.YTD_PERCENTAGE_IN_USD,
        COLUMN.ITD_PERCENT_IN_USD,
    ];
};
const $ = StyleSheet.create({
    mt: {
        marginTop: theme.spacing['0.5'],
    },
});
